/**
 * Deciding what an uploaded file *is* before it is uploaded: a new policy, or
 * the next version of one the library already holds. The guess is by name —
 * "AML Policy v3.pdf" against a library entry called "AML Policy" — and it is
 * only a default: every row in the dialog can be switched by hand.
 *
 * Pure, so the matching can be pinned by tests.
 *
 * @module components/regulation/policyUploadMatch
 */
import { policyDisplayName, type Policy } from "../../state/policies";

/**
 * A comparable form of a document name: extension, version suffixes
 * ("v3", "(2)", "final", "draft"), punctuation and case all stripped, so the
 * same policy re-exported under a slightly different filename still matches.
 */
export function normalizeDocumentName(name: string): string {
  return name
    .replace(/\.pdf$/i, "")
    .replace(/\b(?:v|version|ver)\s*\.?\s*\d+(?:\.\d+)*\b/gi, " ")
    .replace(/\((?:\d+|final|draft|copy)\)/gi, " ")
    .replace(/\b(?:final|draft|copy|updated|new|signed)\b/gi, " ")
    .replace(/[\p{P}\p{S}]+/gu, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/**
 * The library entry a filename most plausibly updates, or null when nothing
 * matches well enough to suggest. Exact normalised match wins; otherwise one
 * name containing the other counts, but only for names long enough that the
 * containment means something ("AML" inside "AML CTF CPF Strategy" does not).
 */
export function suggestVersionTarget(
  filename: string,
  policies: ReadonlyArray<Pick<Policy, "id" | "filename" | "title">>,
): Pick<Policy, "id" | "filename" | "title"> | null {
  const needle = normalizeDocumentName(filename);
  if (needle.length === 0) return null;
  const candidates = policies.map((policy) => ({
    policy,
    name: normalizeDocumentName(policyDisplayName(policy)),
    file: normalizeDocumentName(policy.filename),
  }));
  const exact = candidates.find((entry) => entry.name === needle || entry.file === needle);
  if (exact) return exact.policy;
  if (needle.length < 12) return null;
  const contained = candidates.find(
    (entry) =>
      (entry.name.length >= 12 && (needle.includes(entry.name) || entry.name.includes(needle))) ||
      (entry.file.length >= 12 && (needle.includes(entry.file) || entry.file.includes(needle))),
  );
  return contained?.policy ?? null;
}

/** "1.2 MB" / "480 KB" for a file row. */
export function formatFileSize(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
